/** Up to four digits from a typed or pasted time. */
export function foodSafetyTimeDigits(text) {
  return String(text || '')
    .replace(/\D/g, '')
    .slice(0, 4)
}

/**
 * Digits kept while typing, with the colon put in for them.
 * "1430" and "14:30" both become "14:30". "14" stays "14" until minutes start.
 */
export function formatFoodSafetyTimeEntry(text) {
  const digits = foodSafetyTimeDigits(text)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)}:${digits.slice(2)}`
}

/** Four slots for the __:__ mask. A missing digit is "_". */
export function foodSafetyClockSlots(text) {
  const digits = foodSafetyTimeDigits(text)
  return [0, 1, 2, 3].map(index => digits[index] || '_')
}

/**
 * Typed time → "HH:mm": "9:05", "09:05", "905", "0905" all work. Returns
 * null when it isn't a real 24-hour time.
 */
export function normalizeFoodSafetyTimeText(text) {
  const raw = String(text || '').trim()
  const match = raw.match(/^(\d{1,2}):?(\d{2})$/)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return `${String(hours).padStart(2, '0')}:${match[2]}`
}

/** 24-hour "HH:mm" for a timestamp, whatever the browser's locale; '' if invalid. */
export function formatFoodSafetyClockTime(at) {
  if (!at) return ''
  const date = at instanceof Date ? at : new Date(at)
  if (Number.isNaN(date.getTime())) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** "YYYY-MM-DD" for `now` in the venue's timezone (browser zone if none). */
export function foodSafetyLocalDateNow(timezone, now = new Date()) {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' }
  try {
    if (timezone) options.timeZone = timezone
    return new Intl.DateTimeFormat('en-CA', options).format(now)
  } catch (error) {
    delete options.timeZone
    return new Intl.DateTimeFormat('en-CA', options).format(now)
  }
}

/**
 * A screen loaded on today whose kitchen day has since moved on: the new
 * day's date, else null. Past days being browsed are left alone.
 */
export function resolveFoodSafetyNewToday(
  { localDate, todayLocalDate, timezone } = {},
  now = new Date(),
) {
  if (!localDate || !todayLocalDate || localDate !== todayLocalDate) return null
  const current = foodSafetyLocalDateNow(timezone, now)
  return current > todayLocalDate ? current : null
}
